import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { ArrowRight, FolderOpen, Plus, Search } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useActiveCompany } from "@/hooks/use-active-company";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AREA_DESCRIPTION,
  AREA_LABEL,
  type DocumentationArea,
} from "@/features/electro-docs/types";
import { ProjectModal } from "@/components/projects/ProjectModal";
import { documentationDestination } from "@/components/projects/project-create-flow";

const AREAS: DocumentationArea[] = ["hms", "ks", "fdv"];

export const Route = createFileRoute("/_authenticated/dokumentasjon/$area")({
  component: AreaProjectPicker,
  head: () => ({
    meta: [
      { title: "Arbeidsområde | VERQO" },
      {
        name: "description",
        content: "Velg prosjekt for å arbeide med HMS, KS eller FDV-dokumentasjon.",
      },
    ],
  }),
});

function AreaProjectPicker() {
  const { area } = Route.useParams();
  const navigate = useNavigate();
  const { companyId, activeModuleKey } = useActiveCompany();
  const [search, setSearch] = useState("");
  const [newProject, setNewProject] = useState(false);

  const valid = AREAS.includes(area as DocumentationArea);
  const areaKey = area as DocumentationArea;

  const projectsQ = useQuery({
    queryKey: ["area-projects", companyId, activeModuleKey],
    enabled: Boolean(companyId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, name, project_number, status, address, city, module_key")
        .eq("company_id", companyId!)
        .eq("module_key", activeModuleKey)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data ?? [];
    },
  });

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = projectsQ.data ?? [];
    if (!term) return all;
    return all.filter((p) =>
      [p.name, p.project_number, p.address, p.city]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term)),
    );
  }, [projectsQ.data, search]);

  if (!valid) {
    return (
      <div className="mx-auto max-w-3xl p-4 lg:p-6">
        <Card className="p-6 text-sm text-muted-foreground">
          Ukjent arbeidsområde.{" "}
          <Link to="/portal" className="underline">
            Tilbake til portalen
          </Link>
          .
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{AREA_LABEL[areaKey]}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{AREA_DESCRIPTION[areaKey]}</p>
        </div>
        <Button size="sm" onClick={() => setNewProject(true)}>
          <Plus className="mr-1 h-4 w-4" /> Nytt dokumentoppdrag
        </Button>
      </div>

      {newProject && (
        <ProjectModal
          onClose={() => setNewProject(false)}
          onCreated={({ projectId }) => navigate(documentationDestination(projectId, areaKey))}
        />
      )}

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Søk etter prosjekt…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {projectsQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">Laster prosjekter…</Card>
      ) : rows.length === 0 ? (
        <Card className="space-y-3 p-6 text-sm text-muted-foreground">
          <p>
            Ingen dokumentoppdrag funnet. Et dokumentoppdrag lagres som et prosjekt, slik at kunde,
            adresse, historikk og dokumentasjon holdes samlet.
          </p>
          <Button size="sm" onClick={() => setNewProject(true)}>
            <Plus className="mr-1 h-4 w-4" /> Nytt dokumentoppdrag
          </Button>
        </Card>
      ) : (
        <Card className="divide-y divide-border">
          {rows.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() =>
                navigate({
                  to: "/projects/$id/documentation",
                  params: { id: p.id },
                  search: { tab: areaKey },
                })
              }
              className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-muted/50"
            >
              <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{p.name}</div>
                <div className="text-xs text-muted-foreground">
                  {[p.project_number, p.address, p.city, p.status].filter(Boolean).join(" · ")}
                </div>
              </div>
              <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </Card>
      )}

      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/portal">Tilbake til portalen</Link>
        </Button>
      </div>
    </div>
  );
}
